import { AccessToken } from '../Connection';
import { Logger } from '../logs/Logger';

export interface AccessTokenResult {
    access_token: string;
    token_type: string;
    expires_in?: number;
    refresh_token?: string;
    scope?: string;
}

export interface AccessTokenStored {
    expires: string | null;
    result: AccessTokenResult;
}

export async function refreshOAuth2Token(refreshToken: string): Promise<AccessTokenResult> {
    const body = new URLSearchParams();
    body.set('grant_type', 'refresh_token');
    body.set('refresh_token', refreshToken);
    body.set('client_id', 'terminal');
    const resp = await fetch(import.meta.env.VITE_TOKEN_URL!, {
        method: 'POST',
        body: body,
    });
    if (!resp.ok) {
        let errorBody;
        try {
            errorBody = await resp.json();
        } catch (e) {
            throw new Error('Could not refresh token ' + refreshToken);
        }
        await Logger.error('Refresh token failed.', {}, errorBody);
        throw new Error('Could not refresh token ' + refreshToken + '.');
    }
    return (await resp.json()) as AccessTokenResult;
}

export function getOauth2RedirectUri(state: string): string {
    const url = new URL(import.meta.env.VITE_AUTHORIZATION_URL);
    const urlParams = new URLSearchParams();
    urlParams.set('client_id', import.meta.env.VITE_CLIENT_ID);
    urlParams.set('response_type', 'code');
    urlParams.set('redirect_uri', import.meta.env.VITE_REDIRECT_URL);
    urlParams.set('redirect_uri', import.meta.env.VITE_REDIRECT_URL);
    urlParams.set('state', state);
    urlParams.set('scope', 'terminal');
    url.search = urlParams.toString();
    return url.toString();
}

export async function fetchOAuth2Token(code: string): Promise<AccessTokenResult> {
    const body = new URLSearchParams();
    body.set('grant_type', 'authorization_code');
    body.set('code', code);
    body.set('redirect_uri', import.meta.env.VITE_REDIRECT_URL);
    body.set('client_id', import.meta.env.VITE_CLIENT_ID);
    const resp = await fetch(import.meta.env.VITE_TOKEN_URL!, {
        method: 'POST',
        body: body,
    });
    if (!resp.ok) {
        let errorBody;
        try {
            errorBody = await resp.json();
        } catch (e) {
            throw new Error('Could not refresh token ' + code);
        }
        throw new Error('Could not refresh token ' + code + '. Response: ' + errorBody);
    }
    return (await resp.json()) as AccessTokenResult;
}

export function getAuthorizationCodeFromUrl(urlStr: string | URL): string | null {
    const url = new URL(urlStr);
    return url.searchParams.get('code');
}

export function getStateFromUrl(urlStr: string | URL): string | null {
    const url = new URL(urlStr);
    return url.searchParams.get('state');
}

export function isAuthorizationRequestResponse(url: string) {
    return new URL(url).searchParams.get('code') !== null;
}

export function parseResponseToAccessToken(token: AccessTokenStored): AccessToken {
    return {
        refreshToken: token.result.refresh_token || null,
        accessToken: token.result.access_token,
        tokenType: token.result.token_type,
        expires: token.expires ? new Date(Date.parse(token.expires)) : null,
    };
}

export function parseExpiresIn(expiresIn?: number | null): Date | null {
    if (!expiresIn) {
        return null;
    }
    const exp = new Date();
    exp.setSeconds(exp.getSeconds() + expiresIn);
    return exp;
}
